/**
 * CV document language helpers (body copy, not website UI locale).
 */

const ES_WORDS =
  /\b(experiencia|educaci[oó]n|formaci[oó]n|habilidades|resumen|tel[eé]fono|universidad|licenciatura|grado|actualidad|logros|curr[ií]culum|laboral|puesto|ingenier[oa]|desarrollo)\b/gi;
const EN_WORDS =
  /\b(experience|education|skills|summary|university|bachelor|master|achievements|responsibilities|professional|internship|engineer)\b/gi;

const BODY_KEYS = ['title', 'summary', 'professional_summary', 'location'];

/**
 * Flatten translatable CV fields into one string for language detection.
 *
 * @param {Object} content
 * @returns {string}
 */
export function contentLanguageBlob(content) {
  if (!content || typeof content !== 'object') {
    return '';
  }
  const parts = [];
  BODY_KEYS.forEach((key) => {
    if (typeof content[key] === 'string' && content[key].trim()) {
      parts.push(content[key]);
    }
  });
  const jobs = content.experience || content.work_experience || [];
  if (Array.isArray(jobs)) {
    jobs.forEach((job) => {
      if (!job || typeof job !== 'object') {
        return;
      }
      ['title', 'description', 'duration', 'location'].forEach((key) => {
        const value = job[key];
        if (typeof value === 'string' && value.trim()) {
          parts.push(value);
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item) {
              parts.push(String(item));
            }
          });
        }
      });
      if (Array.isArray(job.achievements)) {
        job.achievements.forEach((item) => {
          if (item) {
            parts.push(String(item));
          }
        });
      }
    });
  }
  if (typeof content.education === 'string' && content.education.trim()) {
    parts.push(content.education);
  } else if (Array.isArray(content.education)) {
    content.education.forEach((row) => {
      if (typeof row === 'string') {
        parts.push(row);
        return;
      }
      if (!row || typeof row !== 'object') {
        return;
      }
      ['degree', 'description', 'date', 'location'].forEach((key) => {
        if (typeof row[key] === 'string' && row[key].trim()) {
          parts.push(row[key]);
        }
      });
    });
  }
  ['skills', 'languages', 'certifications', 'achievements'].forEach((key) => {
    const value = content[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          parts.push(String(item));
        }
      });
    } else if (typeof value === 'string' && value.trim()) {
      parts.push(value);
    }
  });
  return parts.join('\n');
}

/**
 * Guess es vs en from raw PDF/Word text. English only when EN words win.
 *
 * @param {string} text
 * @returns {'es'|'en'}
 */
export function detectLanguageFromText(text) {
  const sample = String(text || '').slice(0, 4000);
  const esHits = (sample.match(ES_WORDS) || []).length;
  const enHits = (sample.match(EN_WORDS) || []).length;
  return enHits > esHits ? 'en' : 'es';
}

/**
 * Guess es vs en from structured CV body copy or a text blob.
 *
 * @param {Object|string} content
 * @returns {'es'|'en'}
 */
export function detectCvLanguage(content) {
  if (typeof content === 'string') {
    return detectLanguageFromText(content);
  }
  return detectLanguageFromText(contentLanguageBlob(content));
}

/**
 * Language for the studio ES/EN toggle after an upload.
 *
 * Prefers the original file text so an English PDF opens with EN selected.
 *
 * @param {Object} [options]
 * @param {string} [options.originalText]
 * @param {Object} [options.content]
 * @param {string} [options.hinted]
 * @param {'es'|'en'} [options.fallback='es']
 * @returns {'es'|'en'}
 */
export function resolveCvDocumentLanguage({
  originalText = '',
  content = null,
  hinted = '',
  fallback = 'es',
} = {}) {
  const original = String(originalText || '').trim();
  if (original.length >= 24) {
    return detectLanguageFromText(original);
  }
  if (content && hasTranslatableContent(content)) {
    return detectCvLanguage(content);
  }
  const hint = String(hinted || '').trim().toLowerCase();
  if (hint === 'en' || hint === 'es') {
    return hint;
  }
  return fallback === 'en' ? 'en' : 'es';
}

/**
 * True when the CV has enough prose to send to the translator.
 *
 * @param {Object} content
 * @returns {boolean}
 */
export function hasTranslatableContent(content) {
  return contentLanguageBlob(content).trim().length >= 12;
}

/**
 * CV JSON without the photo (File or data URI) for API payloads and cache keys.
 *
 * @param {Object} content
 * @returns {Object}
 */
export function stripPhoto(content) {
  if (!content || typeof content !== 'object') {
    return {};
  }
  const { photo, ...rest } = content;
  return rest;
}
